import * as React from "react";
import { AvatarImage } from "./avatar";

interface AvatarImageWithRetryProps extends React.ComponentPropsWithoutRef<typeof AvatarImage> {
  maxRetries?: number;
  retryDelay?: number;
  // Track failed URLs in sessionStorage to avoid retrying rate-limited images
  enableSmartRetry?: boolean;
}

/**
 * Enhanced AvatarImage component that handles rate limiting (429 errors) gracefully
 * by implementing retry logic with exponential backoff and proper error handling.
 */
// Session storage key for tracking rate-limited images
const RATE_LIMITED_KEY = 'avatar_rate_limited';

export const AvatarImageWithRetry = React.forwardRef<
  React.ElementRef<typeof AvatarImage>,
  AvatarImageWithRetryProps
>(({ src, onError, maxRetries = 1, retryDelay = 5000, enableSmartRetry = true, ...props }, ref) => {
  const [imageSrc, setImageSrc] = React.useState<string | undefined>(src);
  const [retryCount, setRetryCount] = React.useState(0);
  const retryTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isRateLimitedRef = React.useRef(false);

  // Check if this URL was recently rate-limited
  const isRateLimited = React.useCallback((url: string | undefined): boolean => {
    if (!enableSmartRetry || !url) return false;
    try {
      const rateLimited = sessionStorage.getItem(RATE_LIMITED_KEY);
      if (rateLimited) {
        const urls = JSON.parse(rateLimited) as string[];
        return urls.includes(url);
      }
    } catch {
      // Ignore errors
    }
    return false;
  }, [enableSmartRetry]);

  // Mark URL as rate-limited
  const markRateLimited = React.useCallback((url: string) => {
    if (!enableSmartRetry) return;
    try {
      const rateLimited = sessionStorage.getItem(RATE_LIMITED_KEY);
      const urls = rateLimited ? (JSON.parse(rateLimited) as string[]) : [];
      if (!urls.includes(url)) {
        urls.push(url);
        // Keep only last 10 URLs to avoid storage bloat
        const recentUrls = urls.slice(-10);
        sessionStorage.setItem(RATE_LIMITED_KEY, JSON.stringify(recentUrls));
      }
    } catch {
      // Ignore errors
    }
  }, [enableSmartRetry]);

  // Reset state when src changes
  React.useEffect(() => {
    // If this URL was previously rate-limited, skip retries but still try to load once
    // (rate limits may have expired)
    if (isRateLimited(src)) {
      isRateLimitedRef.current = true;
      setRetryCount(maxRetries + 1); // Skip retries
    } else {
      isRateLimitedRef.current = false;
      setRetryCount(0);
    }
    
    setImageSrc(src);
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, [src, isRateLimited, maxRetries]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const handleError = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const img = e.currentTarget;
      
      // Check if it's a Google URL (likely rate limited)
      const isGoogleUrl = imageSrc?.includes('googleusercontent.com') || imageSrc?.includes('googleapis.com');
      
      // If we haven't exceeded max retries and it's a Google URL (likely rate limited)
      if (retryCount < maxRetries && isGoogleUrl && !isRateLimitedRef.current && imageSrc) {
        // Mark as rate-limited to prevent future retries
        markRateLimited(imageSrc);
        
        // Exponential backoff: wait longer for each retry (5s, 10s, etc.)
        const delay = retryDelay * Math.pow(2, retryCount);
        
        isRateLimitedRef.current = true;
        retryTimeoutRef.current = setTimeout(() => {
          // Don't add cache-busting for Google URLs - let browser cache handle it
          // Just retry with the same URL after delay
          setRetryCount((prev) => prev + 1);
          isRateLimitedRef.current = false;
        }, delay);
      } else {
        // Max retries exceeded or not a retryable error
        // Mark as rate-limited if it's a Google URL
        if (isGoogleUrl && imageSrc) {
          markRateLimited(imageSrc);
        }
        
        // Call original onError to show fallback
        if (onError) {
          onError(e);
        }
      }
    },
    [imageSrc, retryCount, maxRetries, retryDelay, onError, markRateLimited]
  );

  return (
    <AvatarImage
      ref={ref}
      src={imageSrc}
      onError={handleError}
      // Add loading="lazy" to reduce unnecessary requests
      loading="lazy"
      // Add referrerPolicy to potentially help with caching
      referrerPolicy="no-referrer"
      {...props}
    />
  );
});

AvatarImageWithRetry.displayName = "AvatarImageWithRetry";

